"use client"

import { useEffect, useRef, useState } from "react"
import type { BooruPost } from "@/lib/booru-api"

// Moved out of components/booru-detail-modal.tsx (same state, same effects, same dependencies).
export function useSlideshow({
  open,
  post,
  onNavigate,
  canNavigate,
}: {
  open: boolean
  post: BooruPost | null
  onNavigate?: (direction: "prev" | "next") => void
  canNavigate?: { prev: boolean; next: boolean }
}) {
  const [isSlideshowActive, setIsSlideshowActive] = useState(false)
  const [slideshowShuffle, setSlideshowShuffle] = useState(false)
  const [slideshowInterval, setSlideshowInterval] = useState(3000) // 3 seconds default
  const slideshowTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // This modal instance is reused across posts (not remounted per-post), so without this,
  // turning slideshow on once and later opening a completely different post/gallery would
  // silently carry the "on" state over and start auto-advancing without the user touching
  // anything. Only reset on a fresh open (not on every post.id change), since navigating
  // next/prev during an active slideshow session also changes post.id and must not kill it.
  useEffect(() => {
    if (open) setIsSlideshowActive(false)
  }, [open])

  useEffect(() => {
    if (!isSlideshowActive || !post || !onNavigate || !canNavigate) return

    const advanceToNext = () => {
      if (slideshowShuffle) {
        // Get all available posts (assuming we have access to total posts)
        // For now, we'll just use prev/next navigation with random direction
        const directions =
          canNavigate.next && canNavigate.prev ? ["next", "prev"] : canNavigate.next ? ["next"] : ["prev"]

        if (directions.length > 0) {
          const randomDirection = directions[Math.floor(Math.random() * directions.length)] as "next" | "prev"
          onNavigate(randomDirection)
        }
      } else {
        // Sequential mode - just go to next
        if (canNavigate.next) {
          onNavigate("next")
        }
      }
    }

    slideshowTimeoutRef.current = setTimeout(advanceToNext, slideshowInterval)

    return () => {
      if (slideshowTimeoutRef.current) {
        clearTimeout(slideshowTimeoutRef.current)
      }
    }
  }, [isSlideshowActive, post, onNavigate, canNavigate, slideshowShuffle, slideshowInterval])

  return { isSlideshowActive, setIsSlideshowActive, slideshowShuffle, setSlideshowShuffle, slideshowInterval, setSlideshowInterval }
}
